// 2026-07-02 US-003: pure client-side channel projection over recipients returned by
// the real GetBulkNotificationRecipients API. Audience resolution (container/reference
// matching, all-or-nothing validation) now happens server-side — see
// BulkNotificationService.GetRecipients. This module only decides, per recipient,
// which channel applies given the selected preferred channel + fallback setting.
import { Constants } from 'common/constants';
import {
  Destination,
  DESTINATION_ORDER,
  MatchType,
  NotifChannel,
  RecipientApiResult,
  RecipientRow,
  ResolvedChannel,
} from 'common/models/BulkNotificationTypes';

// Mirrors AssignBarcodes.parseBarcodes: split on comma/newline, trim, drop empties, dedupe.
export const parseRefs = (text: string): string[] => {
  const refs = (text || '')
    .split(/[,\n]/)
    .map((r) => r.trim())
    .filter((r) => r.length > 0);
  return Array.from(new Set(refs));
};

const isValidEmail = (email: string | null): boolean => !!email && Constants.EMAIL_REGEX_INPUT.test(email.trim());

const isValidMobile = (mobile: string | null): boolean => !!mobile && /\d/.test(mobile.trim());

// Bidirectional: preferred channel -> the other channel (when fallback on) -> unreachable.
const projectChannel = (row: RecipientApiResult, channel: NotifChannel, fallbackOn: boolean): ResolvedChannel => {
  if (channel === NotifChannel.Email) {
    if (isValidEmail(row.Email)) return ResolvedChannel.Email;
    if (fallbackOn && isValidMobile(row.Mobile)) return ResolvedChannel.SmsFallback;
    return ResolvedChannel.Unreachable;
  }
  if (isValidMobile(row.Mobile)) return ResolvedChannel.Sms;
  if (fallbackOn && isValidEmail(row.Email)) return ResolvedChannel.EmailFallback;
  return ResolvedChannel.Unreachable;
};

// 2026-08-09 US-005: only the four real regions are trusted from the payload. Anything
// else — null, empty, an unexpected string, or a pre-US-005 response with no Destination
// field at all — becomes Unknown. Never invent a region.
const normalizeDestination = (value: string | null | undefined): string => {
  const trimmed = (value || '').trim();
  const match = DESTINATION_ORDER.find((d) => d.toLowerCase() === trimmed.toLowerCase());
  return match && match !== Destination.Unknown ? match : Destination.Unknown;
};

// Province is what the Province filter groups on. Unknown rows are grouped under
// "Unknown" rather than their raw text, so the filter list stays short and the raw value
// stays visible in the table cell where the operator can actually read it.
export const provinceLabel = (row: RecipientRow): string =>
  row.Destination === Destination.Unknown ? Destination.Unknown : row.Province || Destination.Unknown;

// Provinces actually present in the current audience, alphabetical, Unknown last.
export const provinceOptions = (rows: RecipientRow[]): string[] => {
  const names = Array.from(new Set((rows || []).map(provinceLabel)));
  return names.sort((a, b) => {
    if (a === Destination.Unknown) return 1;
    if (b === Destination.Unknown) return -1;
    return a.localeCompare(b);
  });
};

// A filter narrows the VIEW, never the audience (AC-9). An empty filter set means "all" —
// deliberately a rule rather than a snapshot, so it stays correct if the audience changes.
export const applyFilters = (rows: RecipientRow[], destinations: string[], provinces: string[]): RecipientRow[] => {
  const destSet = new Set(destinations || []);
  const provSet = new Set(provinces || []);
  return (rows || []).filter((r) => {
    if (destSet.size > 0 && !destSet.has(r.Destination)) return false;
    if (provSet.size > 0 && !provSet.has(provinceLabel(r))) return false;
    return true;
  });
};

// 2026-08-09 US-005: the ticked set FOLLOWS the filter. Applying or changing a
// destination/province filter re-ticks exactly what that filter shows, discarding any
// manual unticks made under the previous filter. Stated here rather than inline in the
// component so the rule has one definition and is covered by tests.
//
// The alternative — remembering manual exclusions across filter changes — was rejected:
// it turns the selection into "everything except a hidden list", which cannot be shown
// honestly on screen and is exactly the sort of invisible state that causes a wrong send.
export const selectionForFilter = (rows: RecipientRow[], destinations: string[], provinces: string[]): string[] =>
  applyFilters(rows, destinations, provinces).map((r) => r.RefNo);

export const countByDestination = (rows: RecipientRow[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  (rows || []).forEach((r) => {
    counts[r.Destination] = (counts[r.Destination] || 0) + 1;
  });
  return counts;
};

export interface ProjectRecipientsResult {
  Rows: RecipientRow[];
  ReachableCount: number;
  Unreachable: RecipientRow[];
}

export const projectRecipients = (
  recipients: RecipientApiResult[],
  channel: NotifChannel,
  fallbackOn: boolean
): ProjectRecipientsResult => {
  const rows: RecipientRow[] = (recipients || []).map((r) => ({
    RefNo: r.RefNo,
    CustomerName: r.CustomerName,
    Resolved: projectChannel(r, channel, fallbackOn),
    Email: r.Email,
    Mobile: r.Mobile,
    // 2026-08-09 US-005: anything the backend could not classify — including a payload
    // from the pre-US-005 stored procedure, where these fields are absent entirely —
    // normalises to Unknown here, so nothing downstream has to null-check.
    Destination: normalizeDestination(r.Destination),
    MatchType: r.MatchType || MatchType.Unknown,
    Province: (r.Province || '').trim(),
    Town: (r.Town || '').trim(),
  }));

  const unreachable = rows.filter((r) => r.Resolved === ResolvedChannel.Unreachable);
  return {
    Rows: rows,
    ReachableCount: rows.length - unreachable.length,
    Unreachable: unreachable,
  };
};

// 2026-07-02 US-003: preview-only mirror of the server's dedupe-by-contact rule (see
// BulkNotificationService.Send) — several RefNo rows sharing one email/mobile collapse to
// one actual message. Used so the Confirm modal shows the real number of messages that
// will go out, not the raw reachable-row count (the two can differ once one customer's
// multiple boxes are in the audience).
export const countDedupedContacts = (rows: RecipientRow[]): number => {
  const keys = new Set<string>();
  rows.forEach((r) => {
    if (r.Resolved === ResolvedChannel.Unreachable) return;
    const usesEmail = r.Resolved === ResolvedChannel.Email || r.Resolved === ResolvedChannel.EmailFallback;
    const contact = (usesEmail ? r.Email : r.Mobile) || '';
    keys.add(`${usesEmail ? 'Email' : 'SMS'}:${contact.trim().toLowerCase()}`);
  });
  return keys.size;
};
